/**
 * Bundle 169 hotfix 2 - Pod stability tile for /admin/incidents.
 *
 * Live from `GET /api/admin/incidents/boot-health-report`. Primary
 * metric on the tile is the max detector latency (ms) from the last
 * `/admin/health/cards` cache-miss build - that number IS the
 * event-loop starvation signal Bundle 169 was shipped to defuse.
 *
 * Colour tokens (WCAG-AA on the incidents page background):
 *   Green   `max <= 1500ms` AND `slow_detectors === 0`
 *   Amber   `1500 < max <= 3000` OR `slow_detectors >= 1`
 *   Red     `max > 3000`
 *
 * Clicking the tile opens `PodStabilityDetailModal` (shadcn Dialog).
 * The modal renders the full boot-health-report payload with a
 * Refresh button that re-fetches without busting caches.
 */
import React, { useCallback, useEffect, useState } from 'react';
import { Card, CardContent } from '@/components/ui/card';
import { Activity } from 'lucide-react';
import api from '@/utils/api';
import PodStabilityDetailModal from './PodStabilityDetailModal';
import {
  AMBER_MIN_MS, RED_MIN_MS,
  readMaxDetectorMs, countSlowDetectors, toneFor,
  slowestDetectorName,
} from './PodStabilityTile.helpers';

const borderClasses = {
  green: 'border-neutral-200 hover:border-primary-300',
  amber: 'border-amber-200 hover:border-amber-300',
  red: 'border-red-300 hover:border-red-400',
};

const numberClasses = {
  green: 'text-neutral-500',
  amber: 'text-amber-700',
  red: 'text-red-700',
};

const PodStabilityTile = () => {
  const [report, setReport] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [open, setOpen] = useState(false);

  const load = useCallback(async () => {
    setLoading(true);
    setError('');
    try {
      const { data } = await api.get(
        '/admin/incidents/boot-health-report');
      setReport(data);
    } catch (e) {
      setError(e?.response?.data?.detail || 'Failed to load pod stability.');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { load(); }, [load]);

  const maxMs = readMaxDetectorMs(report);
  const slow = countSlowDetectors(report);
  const tone = toneFor(maxMs, slow);
  const displayMs = maxMs === null ? '-' : maxMs;
  const slowestName = tone === 'green' ? null : slowestDetectorName(report);

  return (
    <>
      <button
        type="button"
        onClick={() => setOpen(true)}
        className="block w-full text-left group"
        data-testid="pod-stability-tile-button"
        aria-label="Open pod stability detail"
      >
        <Card
          className={
            'border shadow-soft transition-all hover:shadow-md '
            + borderClasses[tone]
          }
          data-testid="pod-stability-tile"
          data-tone={tone}
        >
          <CardContent className="p-4">
            <div className="flex items-start justify-between gap-4">
              <div className="min-w-0">
                <div className="flex items-center gap-2 mb-1">
                  <Activity className="h-4 w-4 text-primary-800" />
                  <div className="text-[10px] uppercase tracking-widest text-secondary-700">
                    Pod stability
                  </div>
                </div>
                <div className="text-sm text-primary-900 font-semibold mb-2">
                  {loading
                    ? 'Loading.'
                    : error
                      ? 'Could not load'
                      : tone === 'red'
                        ? 'Detector over 3s - loop starvation risk'
                        : tone === 'amber'
                          ? 'Slow detector detected'
                          : 'Event loop responsive'}
                </div>
                <div className="flex items-baseline gap-3">
                  <span
                    className={`text-2xl font-semibold ${numberClasses[tone]}`}
                    data-testid="pod-stability-tile-max-ms"
                  >
                    {displayMs}
                    {typeof displayMs === 'number' ? (
                      <span className="text-xs font-normal ml-0.5">ms</span>
                    ) : null}
                  </span>
                  <span
                    className="text-[10px] uppercase tracking-widest text-neutral-500"
                    data-testid="pod-stability-tile-slow-count"
                  >
                    Slow detectors {slow}
                  </span>
                </div>
                {slowestName ? (
                  <div
                    className="text-[10px] text-neutral-500 mt-1 font-mono"
                    data-testid="pod-stability-tile-slowest-name"
                  >
                    Slowest: {slowestName}
                  </div>
                ) : null}
                {error ? (
                  <div className="text-xs text-red-700 mt-2">{error}</div>
                ) : null}
              </div>
              <div className="text-xs text-secondary-700 group-hover:text-primary-900 whitespace-nowrap">
                Open detail →
              </div>
            </div>
          </CardContent>
        </Card>
      </button>
      <PodStabilityDetailModal
        open={open}
        onClose={() => setOpen(false)}
        report={report}
        loading={loading}
        error={error}
        onRefresh={load}
      />
    </>
  );
};

export default PodStabilityTile;
export {
  AMBER_MIN_MS, RED_MIN_MS,
  toneFor, readMaxDetectorMs, countSlowDetectors,
} from './PodStabilityTile.helpers';
